import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { Spinner } from './Spinner';
import { buttonVariantClasses } from './button-variants';
import type { ButtonSize, ButtonVariant } from './button-variants';
import { cn } from './utils';

export interface IconButtonProps extends ComponentPropsWithoutRef<'button'> {
  /** Icon buttons always need an accessible name. */
  'aria-label': string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const spinnerSizes: Record<ButtonSize, 'sm' | 'md'> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    variant = 'ghost',
    size = 'md',
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60',
        buttonVariantClasses[variant],
        iconSizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size={spinnerSizes[size]} /> : children}
    </button>
  );
});
